import { observer } from 'mobx-react-lite';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import AttachmentClass from 'owa-attachment-model-store/lib/store/schema/AttachmentClass';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import { getUserConfiguration } from 'owa-session-store';
import { AttachmentPreviewMode } from 'owa-sxs-store';
import { trace } from 'owa-trace';
import * as React from 'react';
import { SurfaceAction } from '../types/SurfaceAction';
import { handleComponentOnBlur, handleComponentOnFocus } from '../utils/handleAttachmentViewFocus';
import { DeleteAttachmentButton } from './AttachmentActionsContainer.locstring.json';
import AttachmentContextMenuButton from './AttachmentContextMenuButton';
import AttachmentSurfaceActionButton from './SurfaceActions/AttachmentSurfaceActionButton';
import { isOfficeFile } from '../utils/isOfficeFile';

export interface AttachmentActionsProps {
    attachmentViewState: AttachmentFullViewState;
    isForImageView: boolean;
    onDeleteButtonClicked: (attachmentViewState: AttachmentFullViewState) => void;
    previewState: AttachmentPreviewMode;
    attachmentClass: AttachmentClass;
    parentItemId: ClientItemId;
}

const classNames = require('classnames');
const styles = require('./AttachmentActionsContainer.scss');

// This component is responsible for showing the attachment context menu button and delete button.
export default observer(function AttachmentActionsContainer(props: AttachmentActionsProps) {
    const keyDown = (event: React.KeyboardEvent<unknown>) => {
        if (event.keyCode === KeyCodes.enter || event.keyCode === KeyCodes.space) {
            trace.info('AttachmentDeleteButton.keyDown enter|space');
            event.stopPropagation();
            event.preventDefault();
            props.onDeleteButtonClicked(props.attachmentViewState);
        }
    };
    const handleOnFocus = () => {
        handleComponentOnFocus(props.attachmentViewState, props.isForImageView);
    };
    const handleOnBlur = () => {
        handleComponentOnBlur(props.attachmentViewState, props.isForImageView);
    };
    const onClickDeleteButton = (ev: React.MouseEvent<unknown>) => {
        ev.stopPropagation();
        props.onDeleteButtonClicked(props.attachmentViewState);
    };
    const {
        attachmentClass,
        attachmentViewState,
        isForImageView,
        previewState,
        parentItemId,
    } = props;
    let deleteButtonClass;
    if (isForImageView) {
        deleteButtonClass = classNames([styles.deleteButton, styles.deleteButtonForImageView]);
    } else {
        deleteButtonClass = classNames(styles.deleteButton, getAttachmentStyle(previewState));
    }
    const isForAddToCalendar =
        attachmentClass === AttachmentClass.CalendarEvent && isFeatureEnabled('cal-ics-preview');
    return (
        <>
            {attachmentViewState.isReadOnly ? null : (
                <div
                    className={classNames(
                        styles.actionContainerFirstLine,
                        styles.actionContainerDisplay
                    )}>
                    <IconButton
                        onFocus={handleOnFocus}
                        onBlur={handleOnBlur}
                        onKeyDown={keyDown}
                        ariaLabel={loc(DeleteAttachmentButton)}
                        className={deleteButtonClass}
                        onClick={onClickDeleteButton}
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                            styles: {
                                root: styles.icon,
                            },
                        }}
                    />
                </div>
            )}
            <div
                className={classNames(
                    styles.secondLineActionsContainer,
                    styles.actionContainerDisplay
                )}>
                {!getUserConfiguration().SessionSettings.IsShadowMailbox && (
                    <AttachmentSurfaceActionButton
                        attachmentViewState={attachmentViewState}
                        isForImageView={isForImageView}
                        previewState={previewState}
                        action={getCustomSurfaceAction(attachmentViewState.attachmentId)}
                        parentItemId={parentItemId}
                    />
                )}
                <AttachmentSurfaceActionButton
                    attachmentViewState={attachmentViewState}
                    isForImageView={isForImageView}
                    previewState={previewState}
                    action={
                        isForAddToCalendar ? SurfaceAction.addToCalendar : SurfaceAction.download
                    }
                    parentItemId={parentItemId}
                />
                <AttachmentContextMenuButton
                    attachmentViewState={attachmentViewState}
                    isForImageView={isForImageView}
                    previewState={previewState}
                />
            </div>
        </>
    );
});

function getCustomSurfaceAction(attachmentId: AttachmentId) {
    if (isFeatureEnabled('woven-newTabExperiment') && isOfficeFile(attachmentId)) {
        return SurfaceAction.openNewTabAndSaveToCloud;
    }

    if (isFeatureEnabled('woven-editAndReplySurfaceAction')) {
        return SurfaceAction.editAndReply;
    }

    return SurfaceAction.saveToCloud;
}

function getAttachmentStyle(previewState: AttachmentPreviewMode): string {
    switch (previewState) {
        case AttachmentPreviewMode.Edit:
            return styles.edit;
        case AttachmentPreviewMode.NotSelectedAndSaving:
            return styles.notSelectedAndSaving;
        case AttachmentPreviewMode.OtherSaving:
            return styles.attachmentView;
        case AttachmentPreviewMode.Selected:
            return styles.selected;
        case AttachmentPreviewMode.SelectedAndPending:
            return styles.selectedAndPending;
        case AttachmentPreviewMode.SelectedAndSaving:
            return styles.selectedAndSaving;
        default:
            return styles.none;
    }
}
